/**
 * Playing a score from React.
 *
 * The schedule is arithmetic and the player is a machine with a clock; what React adds is
 * the frame loop that keeps the two going, and a position that a cursor can be drawn from.
 * The player is deliberately not given a timer of its own -- see `playback/player.ts` -- so
 * this is the only place in the package that knows what a frame is.
 *
 * Editing the score while it plays makes a new schedule, and a schedule is a player's whole
 * contents, so the old player is stopped and replaced. The music stops on an edit. That is
 * honest: the notes that were about to sound are no longer the notes in the document.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { EventId } from "../document/ids";
import type { Score } from "../document/types";
import { clickTrack } from "../playback/click";
import type { TimeRange } from "../playback/locate";
import type { AudioEngine } from "../playback/port";
import { createPlayer } from "../playback/player";
import type { PlayerOptions } from "../playback/player";
import { scheduleOf } from "../playback/schedule";
import type { Schedule, ScheduleOptions } from "../playback/schedule";

export interface PlaybackOptions extends ScheduleOptions, PlayerOptions {
  /** Sound a click on every beat. */
  readonly metronome?: boolean;
  /**
   * Bars of counting before the music starts.
   *
   * The count-in is clicked whether or not the metronome is on for the music itself, which
   * is what a player who wants four beats and then silence is asking for.
   */
  readonly countInBars?: number;
}

export interface Playback {
  readonly schedule: Schedule;
  readonly playing: boolean;
  /** Seconds into the piece, as of the last frame. */
  readonly position: number;
  /**
   * The events sounding now, for highlighting them on the page.
   *
   * Empty whenever the player is not running. Position alone would not say so: stopping puts
   * the playhead back to the start, where the first note of the piece is sounding by the
   * arithmetic, and a note left lit under a stopped transport reads as though the music were
   * still going.
   */
  readonly sounding: readonly EventId[];
  /** Play from `seconds`, or from where it stopped. */
  play(seconds?: number): void;
  pause(): void;
  stop(): void;
  /** Move the position without starting or stopping. */
  seek(seconds: number): void;
  /** Where the position starts: below zero when there is a count-in. */
  readonly start: number;
  /** The stretch being repeated, or `null`. */
  readonly loop: TimeRange | null;
  /** Repeat a stretch of the piece, or stop repeating with `null`. */
  setLoop(range: TimeRange | null): void;
}

const NOTHING: readonly EventId[] = [];

/**
 * A player over a score.
 *
 * A null engine -- no `AudioContext` yet, because a browser will not give one out before
 * the user has clicked something -- still answers with a schedule, so a caller can show the
 * length of the piece and lay out a timeline before any sound is possible.
 */
export function usePlayback(
  engine: AudioEngine | null,
  score: Score,
  options: PlaybackOptions = {},
): Playback {
  const settings = JSON.stringify(options);
  const chosen = useMemo(() => JSON.parse(settings) as PlaybackOptions, [settings]);

  const schedule = useMemo(() => scheduleOf(score, chosen), [score, chosen]);

  // The clicks are worked out here rather than passed in because they are a fact about the
  // schedule, and the schedule is remade on every edit: a caller holding its own click track
  // would be handing back yesterday's beats.
  const clicks = useMemo(() => {
    const counted = Math.max(0, Math.round(chosen.countInBars ?? 0));
    if (chosen.metronome !== true && counted === 0) return [];
    const whole = clickTrack(score, schedule, { countInBars: counted });
    return chosen.metronome === true ? whole : whole.filter((click) => click.start < 0);
  }, [score, schedule, chosen]);

  const player = useMemo(
    () => (engine === null ? null : createPlayer(engine, schedule, { ...chosen, clicks })),
    [engine, schedule, chosen, clicks],
  );

  const start = player?.start() ?? 0;
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(start);
  const [loop, setLoopState] = useState<TimeRange | null>(null);
  const frame = useRef(0);

  // The player is rebuilt whenever the score or the options change, and a loop is a setting
  // of the surface rather than of one player, so it is put back on the new one.
  useEffect(() => {
    player?.setLoop(loop);
  }, [player, loop]);

  // A new player is a new piece of music as far as the old one is concerned: silence it.
  useEffect(() => {
    if (player === null) return;
    return () => {
      player.stop();
      setPlaying(false);
      setPosition(player.start());
    };
  }, [player]);

  useEffect(() => {
    if (player === null || !playing) return;

    const tick = (): void => {
      player.advance();
      const at = player.position();
      setPosition(at);
      if (at >= schedule.seconds) {
        player.stop();
        setPlaying(false);
        setPosition(player.start());
        return;
      }
      frame.current = requestAnimationFrame(tick);
    };

    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [player, playing, schedule.seconds]);

  const play = useCallback(
    (seconds?: number): void => {
      if (player === null) return;
      player.play(seconds);
      setPosition(player.position());
      setPlaying(true);
    },
    [player],
  );

  const pause = useCallback((): void => {
    if (player === null) return;
    player.pause();
    setPosition(player.position());
    setPlaying(false);
  }, [player]);

  const stop = useCallback((): void => {
    if (player === null) return;
    player.stop();
    setPosition(player.start());
    setPlaying(false);
  }, [player]);

  const seek = useCallback(
    (seconds: number): void => {
      const to = Math.max(start, Math.min(seconds, schedule.seconds));
      setPosition(to);
      player?.seek(to);
    },
    [player, schedule.seconds, start],
  );

  const sounding = useMemo<readonly EventId[]>(() => {
    if (player === null || !playing) return NOTHING;
    const seen = new Set<EventId>();
    for (const note of player.soundingAt(position)) seen.add(note.eventId);
    return [...seen];
  }, [player, playing, position]);

  return {
    schedule,
    playing,
    position,
    sounding,
    play,
    pause,
    stop,
    seek,
    start,
    loop,
    setLoop: setLoopState,
  };
}
